const __DEV__ = false;

import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { useTheme, jsx } from '@emotion/react';
import { getStyles, sanitizeProps } from '../../styles/theme/index.js';
import { S } from './styles.js';
import useControlledSwitchWarning from '../../hooks/use-controlled-switch/use-controlled-switch.js';
import useControlledState from '../../hooks/use-controlled-state/use-controlled-state.js';
import useId from '../../hooks/use-auto-id/use-auto-id.js';
import makeId from '../../utils/make-id.js';
import mergeDefaultProps from '../../utils/merge-default-props.js';
import { Inline } from '../Inline/inline.js';
import { composeEventHandlers } from '../../utils/compose-event-handlers.js';
import { isString } from '../../utils/check-type.js';
import { Text } from '../Text/text.js';

var _excluded = ["checked", "children", "containerProps", "dataTestId", "dataTestIdContainer", "defaultChecked", "disabled", "endNode", "id", "justifyContent", "onChange", "onClick", "startNode"];
var ToggleInputInternal = function ToggleInputInternal(props, ref) {
  var resiTheme = useTheme();
  var controlledCheck = props.checked,
      children = props.children,
      _props$containerProps = props.containerProps,
      containerProps = _props$containerProps === void 0 ? {} : _props$containerProps,
      dataTestId = props.dataTestId,
      dataTestIdContainer = props.dataTestIdContainer,
      defaultChecked = props.defaultChecked,
      disabled = props.disabled,
      endNode = props.endNode,
      propsId = props.id,
      justifyContent = props.justifyContent,
      onChange = props.onChange,
      onClick = props.onClick,
      startNode = props.startNode,
      rest = _objectWithoutProperties(props, _excluded);
  var isControlled = React.useRef(typeof controlledCheck !== 'undefined');
  useControlledSwitchWarning(controlledCheck, 'checked', 'ToggleInput');
  var _useControlledState = useControlledState(controlledCheck, defaultChecked !== null && defaultChecked !== void 0 ? defaultChecked : false),
      _useControlledState2 = _slicedToArray(_useControlledState, 2),
      checked = _useControlledState2[0],
      setChecked = _useControlledState2[1];
  var getLabel = function getLabel(node) {
    if (!node) return false;
    return isString(node) ? jsx(Text, null, node) : node;
  };
  var handleChange = function handleChange() {
    if (!isControlled.current && !disabled) {
      setChecked(!checked);
    }
  };
  var providedId = useId(propsId);
  var id = propsId !== null && propsId !== void 0 ? propsId : makeId('toggle-input', providedId);
  var propsAndState = mergeDefaultProps(props, {
    checked: checked
  });
  return jsx("label", _extends({
    css: function css(theme) {
      return [S.container(theme, propsAndState), getStyles('container', theme, propsAndState)];
    },
    "data-testid": dataTestIdContainer
  }, _defineProperty({}, "data-".concat(resiTheme.prefix, "-toggle"), ''), {
    htmlFor: id
  }, containerProps), jsx(Inline, {
    alignItems: "center",
    justifyContent: justifyContent,
    noImportant: true
  }, getLabel(startNode), jsx("input", _extends({
    ref: ref,
    checked: checked,
    css: function css(theme) {
      return [S.control(theme, propsAndState), getStyles('control', theme, propsAndState)];
    },
    "data-testid": dataTestId,
    disabled: disabled,
    id: id,
    onChange: composeEventHandlers(onChange, handleChange),
    onClick: composeEventHandlers(onClick, handleChange),
    role: "switch",
    tabIndex: 0,
    type: "checkbox"
  }, _defineProperty({}, "data-".concat(resiTheme.prefix, "-toggle-input"), ''), sanitizeProps(rest))), jsx("span", _extends({
    css: function css(theme) {
      return [S.track(theme, propsAndState), getStyles('track', theme, propsAndState)];
    }
  }, _defineProperty({}, "data-".concat(resiTheme.prefix, "-toggle-track"), '')), jsx("span", _extends({
    css: function css(theme) {
      return [S.thumb(theme, propsAndState), getStyles('thumb', theme, propsAndState)];
    }
  }, _defineProperty({}, "data-".concat(resiTheme.prefix, "-toggle-thumb"), '')))), getLabel(children || endNode)));
};
var ToggleInput = Object.assign( React.forwardRef(ToggleInputInternal), {
  displayName: 'ToggleInput'
});

export { ToggleInput };
//# sourceMappingURL=input-toggle.js.map
